import { LABEL_CORRECTIVE, LABEL_EVOLUTIVE, LABEL_NON_BILLABLE, TODAY } from '../../constants';
import { edgeFunctionData } from '../../services/edgeFunctionData';
import { fetchHoursByLabel } from '../../services/jiraSync';
import { currentContractYear, isoStr } from '../../utils/dates';
import { effJiraKey } from '../../utils/jira';
import {
  endProbe,
  jiraUnavailableReason,
  joinProbe,
  recordJiraError,
  resetJiraAvailability,
} from './jiraAvailability';

/* Heures Jira d'une maintenance, ventilées par label (correctif, évolutif, non facturable), sur la
   plage exacte de la phase (dates de worklog), puis sur l'année de contrat en cours, au jour près,
   pour les quotas annuels (les heures se reportent d'un mois sur l'autre). Résultat :
   totalH/ByMonth, corrH/ByMonth, evolH/ByMonth, nonFactH/ByMonth, et totalYearH, corrYearH,
   nonFactYearH, evolYearH. */

// Au niveau du module : `api` est recréé à chaque rendu. Un seul chargement en cours par phase,
// même si l'auto-chargement repart.
const inFlight = new Set();

export default function jiraLabelHoursHandlers(deps) {
  const { setMaintenanceLabelHours } = deps;

  // refresh : clic sur « Actualiser », qui retente Jira même s'il était indisponible.
  async function loadLabelHours(phase, client, { refresh = false } = {}) {
    const projKey = effJiraKey(phase, client);
    if (!projKey || inFlight.has(phase.id)) return;
    inFlight.add(phase.id);
    if (refresh) resetJiraAvailability();
    const setPhase = (value) =>
      setMaintenanceLabelHours((prev) => ({ ...prev, [phase.id]: value }));
    setMaintenanceLabelHours((prev) => ({
      ...prev,
      [phase.id]: { ...(prev[phase.id] || {}), loading: true },
    }));
    const wait = joinProbe();
    if (wait) await wait;
    const reason = jiraUnavailableReason();
    if (reason) {
      inFlight.delete(phase.id);
      if (!wait) endProbe();
      return setPhase({ loaded: true, loading: false, error: reason });
    }
    try {
      await fetchPhase(phase, projKey, setPhase);
    } finally {
      if (!wait) endProbe();
    }
  }

  async function fetchPhase(phase, projKey, setPhase) {
    const fetchLabel = async (label, from = phase.dateDebut, to = phase.dateFin) => {
      // Lève l'erreur du serveur : les labels suivants ne sont pas demandés pour rien.
      const data = await edgeFunctionData(
        await fetchHoursByLabel(from || undefined, label, projKey, to || undefined),
      );
      return data?.hours != null
        ? { hours: data.hours, byMonth: data.byMonth || {} }
        : { hours: null, byMonth: {} };
    };
    const res = {};
    const store = async (label, prefix) => {
      const r = await fetchLabel(label);
      res[`${prefix}H`] = r.hours;
      res[`${prefix}ByMonth`] = r.byMonth;
    };
    try {
      // Total du projet sur la plage EXACTE de la phase (dates de worklog), pour ne pas compter
      // les heures d'une autre phase quand deux phases partagent un mois.
      await store(undefined, 'total');
      await store(LABEL_CORRECTIVE, 'corr');
      await store(LABEL_NON_BILLABLE, 'nonFact');
      const isEvol = phase.facturation === 'maintenance_evolutive';
      // Les tags arbitrent correctif vs évolutif ; le reste est non facturable.
      if (isEvol) await store(LABEL_EVOLUTIVE, 'evol');
      const year = currentContractYear(phase.dateDebut, phase.dateFin, isoStr(TODAY));
      if (year) {
        const inYear = async (label) => (await fetchLabel(label, year.from, year.to)).hours;
        res.totalYearH = await inYear(undefined);
        res.corrYearH = await inYear(LABEL_CORRECTIVE);
        res.nonFactYearH = await inYear(LABEL_NON_BILLABLE);
        if (isEvol) res.evolYearH = await inYear(LABEL_EVOLUTIVE);
      }
      setPhase({ ...res, loaded: true, loading: false, error: null });
    } catch (e) {
      recordJiraError(e.message);
      setPhase({ ...res, loaded: true, loading: false, error: e.message });
    } finally {
      inFlight.delete(phase.id);
    }
  }

  return { loadLabelHours };
}
